import {
  Component,
  OwnershipAreas,
  Priority,
} from "@outschool/ownership-areas";
import jsonStringify from "json-stringify-safe";

type SentryTags = {
  priority?: Priority;
  component?: Component;
  [key: string]: any;
};

export type SentryLogOptions = {
  tags?: SentryTags;
  extras?: unknown;
};

export enum SampleRate {
  OnePercent = 0.01,
  FivePercent = 0.05,
  TenPercent = 0.1,
  TwentyPercent = 0.2,
  FiftyPercent = 0.5,
  All = 1,
}

function shouldSample(sampleRate: SampleRate) {
  return Math.random() < sampleRate;
}

function appendOwnershipToTags(tags: SentryTags) {
  const { component } = tags;
  return {
    ...tags,
    owner: component ? OwnershipAreas[component] : undefined,
  };
}

export function logException(ex: unknown, options?: SentryLogOptions) {
  const Sentry = (window as any)?.Sentry;
  Sentry &&
    Sentry.withScope((scope: any) => {
      if (options?.tags) {
        scope.setTags(appendOwnershipToTags(options.tags));
      }
      if (options?.extras) {
        scope.setExtras(options.extras);
      }

      if (!ex) {
        Sentry.captureMessage("Failed to capture an invalid exception");
      } else if (!(ex instanceof Error)) {
        try {
          const message = typeof ex === "string" ? ex : jsonStringify(ex);
          Sentry.captureMessage(message);
        } catch (error) {
          Sentry.captureMessage(
            `Failed to capture a non-Error as a string: ${error}`
          );
        }
      } else {
        Sentry.captureException(ex);
      }
    });

  window.console && window.console.log && window.console.log(ex);
}

export function logMessage(
  message: string,
  options?: SentryLogOptions,
  sampleRate: SampleRate = SampleRate.All
) {
  if (shouldSample(sampleRate)) {
    const Sentry = (window as any)?.Sentry;
    Sentry &&
      Sentry.withScope((scope: any) => {
        if (options?.tags) {
          scope.setTags(appendOwnershipToTags(options.tags));
        }
        if (options?.extras) {
          scope.setExtras(options.extras);
        }
        Sentry.captureMessage(message);
      });
  }

  window.console && window.console.log && window.console.log(message);
}

/**
 * Create a duplicate error instance with the same name and stack trace.
 * Used to prevent Sentry from deduplicating error messages.
 * @param error the error to duplicate
 * @param newMessagePrefix Prepend this message to the start of the original
 * message
 */
export const duplicateError = (error: Error, newMessagePrefix: string) => {
  const wrapperError = new Error(`${newMessagePrefix} ${error.message}`);
  wrapperError.name = error.name;
  wrapperError.stack = error.stack;
  return wrapperError;
};

interface SentryScope {
  setUser: (userObj: Object) => void;
}

/**
 * A simple wrapper around window.Sentry.configureScope
 * @param callback
 */
export const configureScope = (callback: (scope: SentryScope) => void) => {
  const Sentry = (window as any).Sentry;
  Sentry && Sentry.configureScope(callback);
};
